import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {
  FormAnswerDTO,
  ReportFiltersDTO,
} from 'src/app/private/pages/reporting-page/filters/report-filters.dto';
import { TopicDTO } from 'src/app/private/shared/components/topic-select/topic-select.component';
import { CoachingSessionTypeDTO } from 'src/app/private/shared/dtos/coaching-session.dto';
import { CompetencyDTO } from 'src/app/private/shared/dtos/competencies.dto';
import { PdEvent } from 'src/app/private/shared/dtos/pd.dto';
import { DWCohort } from 'src/app/private/shared/types/responses/cohort.responses';
import { ProFormsAPIResponse } from 'src/app/private/shared/types/responses/proforms.responses';
import { UserDTO, UserLiteDTO } from '../../state/user/user.dto';
import { User } from '../../state/user/user.model';
import { dateTimeFromNgbDate } from '../../utilities/date-helpers';
import { booleanNumberFromBoolean } from '../../utilities/enums/boolean-number.enum';
import { DistrictDTO } from '../district.dto';
import { KpiReportFilter } from '../reports.dto';
import { SchoolDTO, SchoolYearDTO } from '../school.dto';

export const translateKpiReportFilterFromFilterDTO = (
  filterDTO: ReportFiltersDTO
): KpiReportFilter[] => {
  const returnArray: KpiReportFilter[] = [];
  Object.keys(filterDTO).forEach((key) => {
    /* eslint-disable-next-line default-case */
    switch (key) {
      case 'dateRangeFilter':
        returnArray.push({
          codename: 'daterange',
          title: 'Date Range',
          value: [
            dateTimeFromNgbDate(
              (filterDTO.dateRangeFilter as Array<NgbDate>)[0]
            ).toFormat('yyyy-MM-dd'),
            dateTimeFromNgbDate(
              (filterDTO.dateRangeFilter as Array<NgbDate>)[1]
            ).toFormat('yyyy-MM-dd'),
          ],
        });
        break;
      case 'logTypeFilter':
        returnArray.push({
          codename: 'session_type',
          title: 'Session Type',
          value: (filterDTO.logTypeFilter as CoachingSessionTypeDTO[]).map(
            (logType) => logType.id.toString()
          ),
        });
        break;
      case 'schoolYearFilter':
        if (filterDTO.schoolYearFilter) {
          returnArray.push({
            codename: 'school_year',
            title: 'School Year',
            value: [filterDTO.schoolYearFilter?.id.toString()],
            displayName: filterDTO.schoolYearFilter?.name,
          });
        }

        break;
      case 'schoolYearsFilter':
        returnArray.push({
          codename: 'school_year',
          title: 'School Year',
          value: (filterDTO.schoolYearsFilter as SchoolYearDTO[]).map((year) =>
            year.id.toString()
          ),
          displayName: (filterDTO.schoolYearsFilter as SchoolYearDTO[])
            .map((year) => year.name)
            .join(','),
        });
        break;
      case 'districtFilter':
        returnArray.push({
          codename: 'district',
          title: 'District',
          value: (filterDTO.districtFilter as DistrictDTO[]).map((district) =>
            district.id.toString()
          ),
          displayName: filterDTO.districtFilter
            ?.map((district) => district.title)
            .join(','),
        });
        break;
      case 'schoolFilter':
        if (filterDTO.schoolFilter) {
          returnArray.push({
            codename: 'school',
            title: 'School',
            value: (filterDTO.schoolFilter as SchoolDTO[]).map((school) =>
              school.id.toString()
            ),
            displayName: filterDTO.schoolFilter
              ?.map((school) => school.title)
              .join(','),
          });
        }
        break;
      case 'cohortFilter':
        returnArray.push({
          codename: 'cohort',
          title: 'Cohort',
          value: (filterDTO.cohortFilter as DWCohort[]).map((cohort) =>
            cohort.id.toString()
          ),
          displayName: (filterDTO.cohortFilter as DWCohort[])
            .map((cohort) => cohort.name)
            .join(','),
        });
        break;
      case 'assignedCoachFilter':
        returnArray.push({
          codename: 'assigned_coach',
          title: 'Assigned Coach',
          value: [(filterDTO.assignedCoachFilter as User).id.toString()],
          displayName: (filterDTO.assignedCoachFilter as User).fullName,
        });
        break;
      case 'coacheesFilter':
        returnArray.push({
          codename: 'user',
          title: 'Coachee',
          value: (filterDTO.coacheesFilter as UserDTO[]).map((coachee) =>
            coachee.id.toString()
          ),
          displayName: filterDTO.coacheesFilter
            ?.map(
              (coachee) =>
                `${coachee.profile.first_name} ${coachee.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'planUserFilter':
        returnArray.push({
          codename: 'user',
          title: 'Plan User',
          value: (filterDTO.planUserFilter as UserDTO[]).map((planUser) =>
            planUser.id.toString()
          ),
          displayName: filterDTO.planUserFilter
            ?.map(
              (planUser) =>
                `${planUser.profile.first_name} ${planUser.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'participants':
        returnArray.push({
          codename: 'user',
          title: 'Participant',
          value: (filterDTO.participants as UserDTO[]).map((participant) =>
            participant.id.toString()
          ),
          displayName: filterDTO.participants
            ?.map(
              (participant) =>
                `${participant.profile.first_name} ${participant.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'eventFilter':
        if (filterDTO.eventFilter) {
          returnArray.push({
            codename: 'pd_event',
            title: 'PD Event',
            value: (filterDTO.eventFilter as PdEvent[]).map((event) =>
              event.id.toString()
            ),
            displayName: filterDTO.eventFilter
              ?.map((event) => event.title)
              .join(','),
          });
        }
        break;

      case 'facilitators':
        if (filterDTO.facilitators) {
          returnArray.push({
            codename: 'pd_facilitator',
            title: 'PD Faciliator',
            value: (filterDTO.facilitators as UserLiteDTO[]).map(
              (facilitator) => facilitator.id.toString()
            ),
            displayName: filterDTO.facilitators
              ?.map((facilitator) => facilitator.email)
              .join(','),
          });
        }
        break;

      case 'sessionOwners':
        if (filterDTO.sessionOwners) {
          returnArray.push({
            codename: 'session_owner',
            title: 'Session Owner',
            value: (filterDTO.sessionOwners as UserLiteDTO[]).map(
              (session_owner) => session_owner.id.toString()
            ),
            displayName: filterDTO.sessionOwners
              ?.map((session_owner) => session_owner.email)
              .join(','),
          });
        }
        break;

      case 'keyword':
        if (filterDTO.keyword) {
          returnArray.push({
            codename: 'keyword',
            title: 'Keyword',
            value: [filterDTO.keyword ? filterDTO.keyword : ''],
            displayName: filterDTO.keyword ? filterDTO.keyword : '',
          });
        }
        break;

      case 'virtualFilter':
        if (filterDTO.virtualFilter !== undefined) {
          returnArray.push({
            codename: 'is_virtual',
            title: 'Is Virtual',
            value: [filterDTO.virtualFilter.toString()],
            displayName:
              filterDTO.virtualFilter.toString() === '1' ? 'Yes' : 'No',
          });
        }
        break;

      case 'completedFilter':
        if (filterDTO.completedFilter !== undefined) {
          returnArray.push({
            codename: 'pd_session_completed',
            title: 'PD Session Completed',
            value: [filterDTO.completedFilter.toString()],
            displayName:
              filterDTO.completedFilter.toString() === '1' ? 'Yes' : 'No',
          });
        }
        break;

      case 'typesFilter':
        returnArray.push({
          codename: 'pd_session_type',
          title: 'PD Session Type',
          value: filterDTO.typesFilter ? filterDTO.typesFilter : [],
          displayName: filterDTO.typesFilter ? filterDTO.typesFilter[0] : '',
        });
        break;
      case 'categories':
        if (filterDTO.categories && filterDTO.categories.length > 0) {
          returnArray.push({
            codename: 'category',
            title: 'Category',
            value: filterDTO.categories ? filterDTO.categories : [],
            displayName: filterDTO.categories ? filterDTO.categories[0] : '',
          });
        }
        break;
      case 'category_options':
        if (
          filterDTO.category_options &&
          filterDTO.category_options.length > 0
        ) {
          returnArray.push({
            codename: 'category_option',
            title: 'Category Option',
            value: filterDTO.category_options ? filterDTO.category_options : [],
            displayName: filterDTO.category_options
              ? filterDTO.category_options[0]
              : '',
          });
        }
        break;
      case 'competencyList':
        returnArray.push({
          codename: 'competency',
          title: 'Competency',
          value: (filterDTO.competencyList as CompetencyDTO[]).map(
            (competency: CompetencyDTO) => competency.id.toString()
          ),
          displayName: (filterDTO.competencyList as CompetencyDTO[])
            .map((competency: CompetencyDTO) => competency.title)
            .join(', '),
        });
        break;
      case 'proFormFilter':
        returnArray.push({
          codename: 'form',
          title: 'Form',
          value: [
            (filterDTO.proFormFilter as ProFormsAPIResponse).id.toString(),
          ],
          displayName: (filterDTO.proFormFilter as ProFormsAPIResponse).title,
        });
        break;
      case 'formAnswersFilter':
        returnArray.push({
          codename: 'question_answer_submitted',
          value: (filterDTO.formAnswersFilter as FormAnswerDTO[]).map(
            (answer) => answer.id.toString()
          ),
        });
        break;
      case 'topicFilter':
        returnArray.push({
          codename: 'rubric',
          value: [(filterDTO.topicFilter as TopicDTO).id.toString()],
        });
        break;
      case 'hasOption':
        returnArray.push({
          codename: 'has_option',
          value: [
            booleanNumberFromBoolean(filterDTO.hasOption as boolean).toString(),
          ],
        });
        break;
      case 'isReviewed':
        returnArray.push({
          codename: 'is_reviewed',
          value: [
            booleanNumberFromBoolean(
              filterDTO.isReviewed as boolean
            ).toString(),
          ],
        });
        break;
    }
  });
  return returnArray;
};
